/**
 * Shared dependencies
 */
import type { StoreFunctions } from '../../shared/types/store';
import { ListWithItems } from '../../shared/types/lists';

/**
 * Internal dependencies
 */
import { editUserList, type EditUserListParams } from '../requests';

/**
 * Creates a new list for the current user and updates the store.
 * Also dispatches a ListEvent to notify other parts of the app.
 *
 * @param {StoreFunctions} fn - Function to update the store state.
 * @param {EditUserListParams} params - The parameters for editing the list.
 * @return {Promise<ListWithItems>} A promise that resolves to the edited user's list.
 */
export async function listsEdit(
	fn: StoreFunctions,
	params: EditUserListParams
): Promise<ListWithItems> {
	const {
		events: { ListEvent },
	} = window.valioApp;

	// Create the user list based on params
	const list = await editUserList(params);

	if (!list) {
		throw new Error(`Failed to edit list with id ${params.listId}`);
	}

	// Update the current list in the store
	fn.set((state) => {
		const existingList = state.data.lists[list.id];
		return {
			data: {
				lists: {
					...state.data.lists,
					[list.id]: {
						...list,
						items: existingList?.items || [],
					},
				},
			},
		};
	});

	// Get the full list data to send in the event
	const editedList = fn.get().data.lists[list.id];

	if (!editedList) {
		throw new Error(`Failed to retrieve edited list with id ${params.listId} from store`);
	}

	// Dispatch the event to notify other parts of the app
	ListEvent.edit({
		data: {
			[list.id]: {
				...editedList,
			},
		},
	});

	return editedList;
}
